import React, { useState, useEffect, useRef } from 'react';
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonButton,
  IonIcon,
  IonProgressBar
} from '@ionic/react';
import { arrowBack, arrowForward } from 'ionicons/icons';
import { useParams, useHistory } from 'react-router-dom';
import { useConfig } from '../hooks/useConfig';
import { useProgress } from '../hooks/useProgress';
import StepRenderer from '../components/StepRenderer';
import CelebrationEffect from '../components/CelebrationEffect';
import { soundService } from '../services/SoundService';
import { hapticService } from '../services/HapticService';
import SEO from '../components/SEO';
import { seoService } from '../services/SEOService';
import './LessonDetail.css';

/**
 * LessonDetail Page
 * Step-by-step lesson view
 */
function LessonDetail() {
  const { id } = useParams();
  const history = useHistory();
  const { config, lessons } = useConfig();
  const { completeLesson, saveLessonProgress, markLessonComplete, updateLessonProgress, addPoints } = useProgress();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [lesson, setLesson] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (lessons?.lessons) {
      const found = lessons.lessons.find(l => l.id === id);
      setLesson(found);
      setCurrentStep(0);
    }
  }, [id, lessons]);

  // Get SEO meta - dynamic per lesson
  const meta = lesson ? seoService.getLessonMeta(lesson) : seoService.getPageMeta('lessons');

  if (!lesson) {
    return (
      <>
        <SEO
          title={meta.title}
          description={meta.description}
          keywords={meta.keywords}
          canonicalUrl={meta.canonicalUrl}
          ogImage={meta.ogImage}
        />
        <IonPage>
          <IonContent className="ion-padding">
            <p>Lesson not found.</p>
          </IonContent>
        </IonPage>
      </>
    );
  }

  const steps = lesson.steps || [];
  const totalSteps = steps.length;
  const progress = totalSteps > 0 ? (currentStep + 1) / totalSteps : 0;

  const handleNext = () => {
    soundService.playClick();
    hapticService.light();
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
      updateLessonProgress(lesson.id, ((currentStep + 2) / totalSteps) * 100);
      // Scroll to top of next step
      contentRef.current?.scrollToTop(300);
    } else {
      // Lesson complete
      handleLessonComplete();
    }
  };

  const handlePrevious = () => {
    soundService.playClick();
    hapticService.light();
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      // Scroll to top of previous step
      contentRef.current?.scrollToTop(300);
    }
  };

  const handleLessonComplete = () => {
    soundService.playSuccess();
    hapticService.success();
    markLessonComplete(lesson.id);
    addPoints(lesson.points || config?.gamification?.pointsPerLesson || 100);
    setShowCelebration(true);
  };

  const handleCelebrationComplete = () => {
    setShowCelebration(false);
    // Navigate to next lesson or back to list
    const currentIndex = lessons.lessons.findIndex(l => l.id === id);
    const nextLesson = lessons.lessons[currentIndex + 1];
    
    if (nextLesson) {
      history.push(`/lessons/${nextLesson.id}`);
    } else {
      history.push('/lessons');
    }
  };

  const handleQuizAnswer = (isCorrect, points) => {
    if (isCorrect) {
      soundService.playSuccess();
      hapticService.success();
      addPoints(points || config?.gamification?.pointsPerQuiz || 25);
    } else {
      soundService.playError();
      hapticService.error();
    }
  };

  return (
    <>
      <SEO
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={meta.canonicalUrl}
        ogImage={meta.ogImage}
        structuredData={meta.structuredData}
      />
      <IonPage>
        <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/lessons" />
          </IonButtons>
          <IonTitle>{lesson.title}</IonTitle>
        </IonToolbar>
        <IonProgressBar value={progress} />
      </IonHeader>

      <IonContent className="lesson-detail-content" ref={contentRef}>
        {/* Step Counter */}
        <div className="step-counter">
          Step {currentStep + 1} of {totalSteps}
        </div>

        {/* Current Step */}
        {steps[currentStep] && (
          <StepRenderer
            step={steps[currentStep]}
            language={config?.app?.language || 'python'}
            onComplete={handleNext}
            onAnswer={handleQuizAnswer}
          />
        )}

        {/* Navigation */}
        <div className="lesson-navigation">
          <IonButton 
            fill="outline" 
            onClick={handlePrevious}
            disabled={currentStep === 0}
          >
            <IonIcon icon={arrowBack} slot="start" />
            Previous
          </IonButton>

          <IonButton 
            onClick={handleNext}
          >
            {currentStep === totalSteps - 1 ? 'Complete' : 'Next'}
            <IonIcon icon={arrowForward} slot="end" />
          </IonButton>
        </div>

        {/* Celebration Effect */}
        <CelebrationEffect
          active={showCelebration}
          message={`🎉 Lesson Complete! +${lesson.points || 100} XP`}
          onComplete={handleCelebrationComplete}
        />
      </IonContent>
    </IonPage>
    </>
  );
}

export default LessonDetail;